import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div style={page}>
      <h1 style={heading}>Privacy Policy</h1>
      <p style={updated}>Last updated: {LAST_UPDATED}</p>

      <p style={disclaimer}>
        This is a general template, not legal advice. Have a lawyer review it
        before relying on it for compliance in your country.
      </p>

      <Section title="1. Information we collect">
        <ul style={list}>
          <li>Account info: email address, username, password (stored securely, never in plain text)</li>
          <li>Date of birth: collected only to confirm you're 18 or older — never shown to anyone, including other users</li>
          <li>Profile info you add: bio, profile photo, gender (optional and private — never shown to anyone)</li>
          <li>Content you post: designs, images, embroidery files, captions, comments</li>
          <li>Activity: likes, downloads, saves, follows, blocks, reports</li>
          <li>Device info for push notifications, if you enable them</li>
        </ul>
      </Section>

      <Section title="2. How we use your information">
        We use this information to run the Service — showing your feed,
        notifying you of activity on your posts, letting you follow other
        users, and enforcing these terms (for example, when you block or
        report someone).
      </Section>

      <Section title="3. Who we share it with">
        We don't sell your personal information. We use third-party
        infrastructure providers (such as Supabase for hosting and the
        database, and web push services for notifications) to run the
        Service — they process data on our behalf and don't use it for their
        own purposes.
      </Section>

      <Section title="4. Cookies and local storage">
        We use your browser's local storage for things like remembering your
        recent searches and your light/dark theme choice. This stays on your
        device and isn't sent to us.
        <br /><br />
        We use Google Analytics to understand how people use the Service,
        for example which pages are visited and what kind of device or
        browser is used. Google Analytics uses cookies or similar identifiers
        and Google processes this information on our behalf. You can opt out
        by installing the{" "}
        <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noreferrer" style={link}>
          Google Analytics Opt-out Browser Add-on
        </a>.
        <br /><br />
        We may show advertising on the Service. Third-party vendors, including
        Google, use cookies to serve ads based on your prior visits to this
        website or other websites. Google's use of advertising cookies
        enables it and its partners to serve ads to you based on your visit
        to this site and/or other sites on the Internet. You can opt out of
        personalized advertising by visiting{" "}
        <a href="https://www.google.com/settings/ads" target="_blank" rel="noreferrer" style={link}>
          Google's Ads Settings
        </a>
        , or opt out of third-party vendors' use of cookies for personalized
        advertising by visiting{" "}
        <a href="https://www.aboutads.info" target="_blank" rel="noreferrer" style={link}>
          www.aboutads.info
        </a>.
      </Section>

      <Section title="5. Data retention and deletion">
        We keep your information as long as your account is active. You can
        delete your account at any time from Profile settings — this
        permanently removes your profile, posts, and account data. This
        action can't be undone.
      </Section>

      <Section title="6. Security">
        We take reasonable steps to protect your information, but no method
        of storage or transmission is 100% secure. Don't reuse your
        GatherYou password anywhere else.
      </Section>

      <Section title="7. Age requirement">
        The Service requires you to be at least 18 years old — this is
        checked (via your date of birth) when you create an account. We don't
        knowingly allow anyone under 18 to use the Service. If you believe an
        underage user has created an account, contact us so we can remove it.
      </Section>

      <Section title="8. Your rights">
        You can view and edit most of your information directly in the app
        (Profile settings). You can request a copy of your data or ask us to
        delete it by deleting your account.
      </Section>

      <Section title="9. Changes to this policy">
        We may update this policy from time to time. Continued use of the
        Service after changes means you accept the updated policy.
      </Section>

      <Section title="10. Contact">
        Questions about this policy? Email us at{" "}
        <a href="mailto:contact@gatheryou.in" style={link}>contact@gatheryou.in</a>.
      </Section>

      <p style={backLink}>
        <Link to="/terms" style={link}>Terms of Service</Link>
        {" · "}
        <Link to="/help" style={link}>Help &amp; FAQ</Link>
        {" · "}
        <Link to="/" style={link}>Back to GatherYou</Link>
      </p>
    </div>
  );
}

const LAST_UPDATED = "November 2026";

function Section({ title, children }) {
  return (
    <section style={section}>
      <h2 style={sectionTitle}>{title}</h2>
      <div style={sectionBody}>{children}</div>
    </section>
  );
}

const page = {
  padding: "24px 20px calc(90px + env(safe-area-inset-bottom))",
  maxWidth: 680,
  margin: "0 auto",
  background: "var(--bg-page)",
  minHeight: "100vh",
};

const heading = {
  fontFamily: "'Fraunces', serif",
  fontSize: 26,
  color: "var(--text-heading)",
  margin: "0 0 4px",
};

const updated = { fontSize: 12.5, color: "var(--text-muted)", margin: "0 0 16px" };

const disclaimer = {
  fontSize: 12.5,
  color: "var(--text-body)",
  background: "var(--bg-soft)",
  border: "1px solid var(--border-soft)",
  borderRadius: 10,
  padding: "10px 14px",
  marginBottom: 24,
};

const section = { marginBottom: 22 };

const sectionTitle = {
  fontSize: 15.5,
  color: "var(--text-heading)",
  margin: "0 0 8px",
};

const sectionBody = {
  fontSize: 14,
  color: "var(--text-body)",
  lineHeight: 1.6,
};

const list = { margin: "8px 0 0", paddingLeft: 20 };

const backLink = {
  fontSize: 13.5,
  color: "var(--text-muted)",
  marginTop: 32,
  textAlign: "center",
};

const link = { color: "var(--accent-link)", fontWeight: 600, textDecoration: "none" };
